import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Formatter } from "utils/helpers/Format";

import Warning from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/DocumentResults/ResultsControls/Warning/Warning";
import ExportMenu from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/components/ExportMenu/ExportMenu";
import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import {
  ExecutionType,
  QueryStatement,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import { MAX_DISPLAY_LENGTH } from "components/Datagrid/constant";
import TooltipMessage from "components/Tooltip/TooltipMessage/TooltipMessage";
import TooltipMessageDivider from "components/Tooltip/TooltipMessage/TooltipMessageDivider";

import { useProactiveWarnings } from "../../hooks/useProactiveWarnings";
import { ExpandControls } from "../ExpandControls";

import styles from "./ResultsControls.module.scss";

interface ResultsControlsProps {
  queryStatement: QueryStatement;
  document: WorkspaceDocument;
  isProfile?: boolean;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}

const ResultsControls = (props: ResultsControlsProps) => {
  const { qsResults } = useDocuments();
  const { document, queryStatement, isProfile, expanded, setExpanded } = props;
  const { hasProactiveWarnings, warningComponent } = useProactiveWarnings({
    queryStatementId: queryStatement.id,
  });

  const results = qsResults[queryStatement.id];

  const { t } = useTranslation();
  const warnings = useMemo(() => {
    const messages = [] as React.ReactElement[];

    if (!results) {
      return messages;
    }

    const duplicatedNamesSet = new Set();
    results.meta?.forEach((field: { name: string; displayName: string }) => {
      if (field.name !== field.displayName) {
        duplicatedNamesSet.add(field.displayName);
      }
    });

    if (duplicatedNamesSet.size) {
      const message = (
        <TooltipMessage
          title={t("queries.duplicated_message", {
            columnName: Array.from(duplicatedNamesSet).join(", "),
          })}
        />
      );

      if (messages.length) messages.push(<TooltipMessageDivider />);

      messages.push(message);
    }

    if (results.rows && results.rows >= MAX_DISPLAY_LENGTH) {
      if (messages.length) messages.push(<TooltipMessageDivider />);

      messages.push(
        <TooltipMessage title={t("statistics.results_display_limit")} />
      );
    }

    return messages;
  }, [t, results]);

  const getExportData = () => {
    const isExplain =
      queryStatement.executionType === ExecutionType.Explain ||
      document.execution?.executionType === ExecutionType.Explain;

    if (isExplain && queryStatement.explainType) {
      return {
        rows: queryStatement.explain?.[queryStatement.explainType]
          ? [queryStatement.explain?.[queryStatement.explainType]]
          : [],
        meta: [],
      };
    }
    if (isProfile) {
      return {
        rows: queryStatement.explain?.analyze
          ? [queryStatement.explain?.analyze]
          : [],
        meta: [],
      };
    }

    return {
      rows: results?.data || [],
      meta: results?.meta || [],
    };
  };

  const exportData = getExportData();

  const showDisplayLimit =
    !!results?.rows && results.rows >= MAX_DISPLAY_LENGTH;

  return (
    <div className={styles.resultControls}>
      {!!queryStatement.statistics && (
        <div className={styles.statistics}>
          {t("statistics.header_duration")}:{" "}
          {Formatter.secondsToTimeString(
            queryStatement.statistics.executionTimeSec ?? 0,
            false,
            false,
            true
          )}
        </div>
      )}
      {hasProactiveWarnings && (
        <>
          {warningComponent}
          <div className={styles.divider} />
        </>
      )}
      {!!warnings.length && (
        <>
          <Warning messages={warnings} />
          <div className={styles.divider} />
        </>
      )}
      {!isProfile && (
        <div
          id="results-column-filter-icon"
          className={styles.datagridMetadata}
        />
      )}
      <ExportMenu
        exportData={exportData}
        showDisplayLimit={showDisplayLimit}
      />

      <ExpandControls
        expanded={expanded}
        setExpanded={setExpanded}
      />
    </div>
  );
};

export default ResultsControls;
